import { useEffect, useMemo, useRef, useState } from 'react';
import { setVote, clearVote } from './pollCookies';

const SIMULATED_API_MS = 750;

const formatNumber = (n) => n.toLocaleString();

const PollItem = ({ poll, votedOptionId, onVoteChange }) => {
  const isOngoing = poll.status === 'ongoing';
  const [showOptions, setShowOptions] = useState(isOngoing && !votedOptionId);
  const [pendingOptionId, setPendingOptionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [barsArmed, setBarsArmed] = useState(!showOptions);
  const submitTimer = useRef(null);

  useEffect(
    () => () => {
      if (submitTimer.current) clearTimeout(submitTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (showOptions) {
      setBarsArmed(false);
      return undefined;
    }
    setBarsArmed(false);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setBarsArmed(true));
    });
    return () => cancelAnimationFrame(id);
  }, [showOptions, votedOptionId]);

  const displayedOptions = useMemo(() => {
    if (!isOngoing || !votedOptionId) return poll.options;
    return poll.options.map((opt) =>
      opt.id === votedOptionId ? { ...opt, votes: opt.votes + 1 } : opt,
    );
  }, [poll.options, isOngoing, votedOptionId]);

  const totalVotes = displayedOptions.reduce((sum, o) => sum + o.votes, 0);
  const winningId = useMemo(() => {
    if (poll.status !== 'closed') return null;
    return poll.options.reduce(
      (top, o) => (o.votes > (top?.votes || -1) ? o : top),
      null,
    )?.id;
  }, [poll.options, poll.status]);

  const handleVote = (optionId) => {
    if (submitting) return;
    setPendingOptionId(optionId);
    setSubmitting(true);
    submitTimer.current = setTimeout(() => {
      setVote(poll.id, optionId);
      onVoteChange(poll.id, optionId);
      setSubmitting(false);
      setPendingOptionId(null);
      setShowOptions(false);
    }, SIMULATED_API_MS);
  };

  const handleChangeVote = () => {
    clearVote(poll.id);
    onVoteChange(poll.id, null);
    setShowOptions(true);
  };

  const showVotingUI = isOngoing && showOptions;

  return (
    <li className={`poll-card poll-card--${poll.status}`}>
      <div className="poll-card__head">
        {isOngoing && (
          <span className="poll-live">
            <span className="poll-live__dot" /> LIVE
          </span>
        )}
        {poll.status === 'closed' && (
          <span className="poll-closed-badge">已结束</span>
        )}
        {submitting && <span className="poll-submitting">提交中…</span>}
      </div>
      <h3 className="poll-card__question">{poll.question}</h3>

      {showVotingUI ? (
        <div className={`poll-options${submitting ? ' is-submitting' : ''}`}>
          {poll.options.map((opt) => {
            const isPending = opt.id === pendingOptionId;
            return (
              <button
                key={opt.id}
                type="button"
                className={`poll-option-btn${isPending ? ' is-pending' : ''}`}
                onClick={() => handleVote(opt.id)}
                disabled={submitting}
              >
                <span className="poll-option-btn__label">{opt.label}</span>
                {isPending && (
                  <span
                    className="poll-option-btn__spinner"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <ul className="poll-results">
          {displayedOptions.map((opt, i) => {
            const pct = totalVotes > 0 ? (opt.votes / totalVotes) * 100 : 0;
            const isMine = isOngoing && opt.id === votedOptionId;
            const isWinner = !isOngoing && opt.id === winningId;
            return (
              <li
                key={opt.id}
                className={`poll-result${isMine ? ' is-mine' : ''}${isWinner ? ' is-winner' : ''}`}
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <div className="poll-result__row">
                  <span className="poll-result__label">{opt.label}</span>
                  <span className="poll-result__pct">{pct.toFixed(1)}%</span>
                </div>
                <div className="poll-result__track">
                  <div
                    className="poll-result__fill"
                    style={{
                      width: `${barsArmed ? pct : 0}%`,
                      transitionDelay: `${i * 70}ms`,
                    }}
                  />
                </div>
                <div className="poll-result__votes">
                  {formatNumber(opt.votes)} 票
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="poll-card__foot">
        <span className="poll-total">共 {formatNumber(totalVotes)} 票</span>
        {isOngoing && !showOptions && votedOptionId && (
          <button
            type="button"
            className="poll-change-vote"
            onClick={handleChangeVote}
          >
            更改投票
          </button>
        )}
        {poll.status === 'closed' && poll.closedAt && (
          <span className="poll-closed-at">截至 {poll.closedAt}</span>
        )}
      </div>
    </li>
  );
};

export default PollItem;
